import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, Checkbox,
    Chip, InputAdornment, Tooltip, useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

export type GSTCategory = { id: number; name: string; code: string };

type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (categoryIds: number[]) => void;
    fieldLabel: string;
    fieldCode: string;
    categories: GSTCategory[];
    currentIds: number[];
    mappings: Record<string, number[]>;
    fieldLabels: Record<string, string>;
};

const GSTCategoryModal: React.FC<Props> = ({
    open, onClose, onSave,
    fieldLabel, fieldCode,
    categories, currentIds, mappings, fieldLabels,
}) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedIds(currentIds);
            setSearch('');
        }
    }, [open, currentIds]);

    // categories linked to OTHER fields
    const linkedElsewhere = useMemo(() => {
        const map: Record<number, string> = {};
        for (const [code, ids] of Object.entries(mappings)) {
            if (code === fieldCode) continue;
            for (const id of ids) {
                map[id] = code;
            }
        }
        return map;
    }, [mappings, fieldCode]);

    const filtered = useMemo(() =>
        categories.filter(c =>
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.code.toLowerCase().includes(search.toLowerCase())
        ),
        [categories, search]
    );

    const selectableFiltered = filtered.filter(c => !linkedElsewhere[c.id]);
    const allSelected = selectableFiltered.length > 0 && selectableFiltered.every(c => selectedIds.includes(c.id));

    const toggleOne = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleAll = () => {
        const selectableIds = selectableFiltered.map(c => c.id);
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !selectableIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...selectableIds])));
        }
    };

    const removeChip = (id: number) => {
        setSelectedIds(prev => prev.filter(x => x !== id));
    };

    const selectedCategories = categories.filter(c => selectedIds.includes(c.id));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const content = (
        <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography fontWeight={600} fontSize={16}>{fieldLabel}</Typography>
                <Typography fontWeight={700} fontSize={16} color="text.secondary">{fieldCode}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Typography fontSize={12} color="text.secondary" mb={1}>Select Categories</Typography>

                {/* Chips for selected */}
                {selectedCategories.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mb={1.5}>
                        {selectedCategories.map(c => (
                            <Chip
                                key={c.id}
                                label={c.name}
                                size="small"
                                onDelete={() => removeChip(c.id)}
                                sx={{ background: '#e8f5f2', color: 'var(--color-primary)', fontSize: 12 }}
                            />
                        ))}
                    </Box>
                )}

                {/* Search */}
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16, color: 'var(--color-text-muted)' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 1 }}
                />

                {/* List */}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    {/* Select All */}
                    <Box
                        sx={{
                            display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                            borderBottom: '1px solid #eee', background: 'var(--color-container-background)',
                            cursor: 'pointer',
                        }}
                        onClick={toggleAll}
                    >
                        <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={!allSelected && selectableFiltered.some(c => selectedIds.includes(c.id))}
                            onChange={toggleAll}
                            onClick={e => e.stopPropagation()}
                            sx={{ p: 0.25, mr: 1, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, '&.MuiCheckbox-indeterminate': { color: 'var(--color-primary)' } }}
                        />
                        <Typography fontSize={13} fontWeight={600} color="var(--color-primary)">Select All</Typography>
                    </Box>

                    {/* Category rows */}
                    <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                        {filtered.map(cat => {
                            const otherCode = linkedElsewhere[cat.id];
                            const isDisabled = !!otherCode;
                            const isChecked = selectedIds.includes(cat.id);

                            const row = (
                                <Box
                                    key={cat.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                                        borderBottom: '1px solid #f5f5f5',
                                        cursor: isDisabled ? 'default' : 'pointer',
                                        opacity: isDisabled ? 0.5 : 1,
                                        '&:hover': { background: isDisabled ? 'transparent' : 'var(--color-container-background)' },
                                    }}
                                    onClick={() => !isDisabled && toggleOne(cat.id)}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={isChecked}
                                        disabled={isDisabled}
                                        onChange={() => !isDisabled && toggleOne(cat.id)}
                                        onClick={e => e.stopPropagation()}
                                        sx={{ p: 0.25, mr: 1, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                    />
                                    <Typography fontSize={13}>{cat.name}</Typography>
                                </Box>
                            );

                            if (isDisabled) {
                                return (
                                    <Tooltip
                                        key={cat.id}
                                        title={`This category is already linked to ${fieldLabels[otherCode] ?? otherCode}`}
                                        placement="right"
                                        arrow
                                    >
                                        <span>{row}</span>
                                    </Tooltip>
                                );
                            }
                            return row;
                        })}
                        {filtered.length === 0 && (
                            <Typography fontSize={13} color="text.secondary" sx={{ px: 2, py: 2 }}>
                                No categories found
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => onSave(selectedIds)}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Save
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} heightVh={80}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default GSTCategoryModal;
